import React, { useState, useEffect, useRef, useCallback } from 'react';
import config from '../config';
import './MCPAppView.css';

/**
 * MCPAppView Component
 * 
 * Implements the Model Context Protocol (MCP) Apps specification for rendering
 * interactive HTML-based user interfaces within Mia Flow's AI chat.
 * 
 * Features:
 * - Iframe sandbox container with configurable CSP
 * - JSON-RPC 2.0 postMessage bridge (host ↔ app)
 * - Lifecycle management: initialize → tool-input → tool-result
 * - Display modes: inline, fullscreen, pip (picture-in-picture)
 * - Theming support (dark/light mode with CSS variables)
 * - Security: sandbox attributes and origin validation
 * 
 * @param {Object} props
 * @param {string} props.resourceUri - The ui:// resource URI
 * @param {Object} props.toolInput - Tool arguments to pass to app
 * @param {Object} props.toolResult - Tool execution result
 * @param {Object} props.hostContext - Host context (theme, project, etc.)
 * @param {Function} props.onSendMessage - Callback for app → chat messages
 * @param {Function} props.onCallTool - Callback for app → tool execution
 * @param {Function} props.onUpdateContext - Callback for context updates
 * @param {Function} props.onOpenLink - Callback for link opening
 * @param {Function} props.onRequestMode - Callback for display mode changes
 */
const MCPAppView = ({
  resourceUri,
  toolInput = null,
  toolResult = null,
  hostContext = {},
  onSendMessage = () => {},
  onCallTool = () => {},
  onUpdateContext = () => {},
  onOpenLink = () => {},
  onRequestMode = () => {}
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [displayMode, setDisplayMode] = useState('inline');
  const [initialized, setInitialized] = useState(false);
  const iframeRef = useRef(null);
  const messageIdCounter = useRef(0);
  const pendingRequests = useRef(new Map());

  // Load UI resource from backend
  const loadResource = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(
        `${config.bffUrl}/api/mcp/ui/resources/read?uri=${encodeURIComponent(resourceUri)}`,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to load resource: ${response.statusText}`);
      }

      const data = await response.json();
      const resourceContent = data.contents?.[0];

      if (!resourceContent) {
        throw new Error('Resource not found');
      }

      // Create blob URL for iframe
      const blob = new Blob([resourceContent.text], { type: 'text/html' });
      const blobUrl = URL.createObjectURL(blob);

      // Load into iframe
      if (iframeRef.current) {
        iframeRef.current.src = blobUrl;
      }

      setLoading(false);
    } catch (err) {
      console.error('Error loading MCP App resource:', err);
      setError(err.message);
      setLoading(false);
    }
  }, [resourceUri]);

  // Generate unique message ID
  const generateMessageId = useCallback(() => {
    return `msg-${++messageIdCounter.current}`;
  }, []);

  // Send JSON-RPC message to app
  const sendToApp = useCallback((method, params, id = null) => {
    if (!iframeRef.current?.contentWindow) {
      console.warn('Cannot send message: iframe not ready');
      return null;
    }

    const msgId = id || generateMessageId();
    const message = {
      jsonrpc: '2.0',
      method,
      id: msgId,
      ...(params && { params })
    };

    iframeRef.current.contentWindow.postMessage(message, '*');
    return msgId;
  }, [generateMessageId]);

  // Handle messages from app
  const handleMessage = useCallback((event) => {
    // Validate origin (blob: or data: for sandboxed iframes)
    const allowedOrigins = ['blob:', 'data:'];
    if (!allowedOrigins.some(origin => event.origin.startsWith(origin))) {
      console.warn('Blocked message from unauthorized origin:', event.origin);
      return;
    }

    const msg = event.data;

    // Validate JSON-RPC structure
    if (!msg || msg.jsonrpc !== '2.0') {
      return;
    }

    // Handle responses to host requests
    if (msg.id && pendingRequests.current.has(msg.id)) {
      const resolver = pendingRequests.current.get(msg.id);
      pendingRequests.current.delete(msg.id);
      
      if (msg.result !== undefined) {
        resolver.resolve(msg.result);
      } else if (msg.error) {
        resolver.reject(new Error(msg.error.message || 'Request failed'));
      }
      return;
    }

    // Handle app notifications
    switch (msg.method) {
      case 'ui/notifications/initialized':
        console.log('MCP App initialized');
        setInitialized(true);
        
        // Send tool input if available
        if (toolInput) {
          sendToApp('ui/notifications/tool-input', toolInput);
        }
        
        // Send tool result if available
        if (toolResult) {
          sendToApp('ui/notifications/tool-result', toolResult);
        }
        break;

      case 'ui/send-message':
        if (msg.params?.message) {
          onSendMessage(msg.params.message);
        }
        break;

      case 'ui/open-link':
        if (msg.params?.url) {
          onOpenLink(msg.params.url);
        }
        break;

      case 'ui/request-mode':
        if (msg.params?.mode) {
          const newMode = msg.params.mode;
          setDisplayMode(newMode);
          onRequestMode(newMode);
        }
        break;

      case 'ui/update-context':
        if (msg.params?.updates) {
          onUpdateContext(msg.params.updates);
        }
        break;

      case 'tools/call':
        if (msg.params?.name) {
          onCallTool(msg.params.name, msg.params.arguments || {})
            .then(result => {
              // Send result back to app
              if (iframeRef.current?.contentWindow) {
                iframeRef.current.contentWindow.postMessage({
                  jsonrpc: '2.0',
                  id: msg.id,
                  result
                }, '*');
              }
            })
            .catch(error => {
              // Send error back to app
              if (iframeRef.current?.contentWindow) {
                iframeRef.current.contentWindow.postMessage({
                  jsonrpc: '2.0',
                  id: msg.id,
                  error: {
                    code: -32603,
                    message: error.message || 'Tool execution failed'
                  }
                }, '*');
              }
            });
        }
        break;

      case 'logging/log':
        // Log messages from app
        const level = msg.params?.level || 'info';
        const logMsg = msg.params?.message || '';
        console.log(`[MCP App ${level.toUpperCase()}]`, logMsg);
        break;

      default:
        console.log('Unhandled MCP App message:', msg);
    }
  }, [toolInput, toolResult, onSendMessage, onOpenLink, onRequestMode, onUpdateContext, onCallTool, sendToApp]);

  // Initialize app with host capabilities
  const initializeApp = useCallback(() => {
    if (!iframeRef.current?.contentWindow) {
      return;
    }

    const initMessage = {
      jsonrpc: '2.0',
      method: 'ui/initialize',
      id: 'init-' + Date.now(),
      params: {
        protocolVersion: '2026-01-26',
        hostCapabilities: {
          serverTools: {},
          logging: {}
        },
        hostContext: {
          theme: hostContext.theme || 'light',
          locale: hostContext.locale || 'en',
          ...hostContext
        }
      }
    };

    iframeRef.current.contentWindow.postMessage(initMessage, '*');
  }, [hostContext]);

  // Setup message listener
  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleMessage]);

  // Load resource on mount
  useEffect(() => {
    loadResource();
  }, [loadResource]);

  // Initialize app when iframe loads
  const handleIframeLoad = useCallback(() => {
    // Wait a bit for iframe scripts to be ready
    setTimeout(() => {
      initializeApp();
    }, 100);
  }, [initializeApp]);

  // Update tool input
  useEffect(() => {
    if (initialized && toolInput) {
      sendToApp('ui/notifications/tool-input', toolInput);
    }
  }, [initialized, toolInput, sendToApp]);

  // Update tool result
  useEffect(() => {
    if (initialized && toolResult) {
      sendToApp('ui/notifications/tool-result', toolResult);
    }
  }, [initialized, toolResult, sendToApp]);

  // Render loading state
  if (loading) {
    return (
      <div className={`mcp-app-container mcp-app-${displayMode}`}>
        <div className="mcp-app-loading">
          <div className="mcp-app-spinner"></div>
          <p>Loading MCP App...</p>
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className={`mcp-app-container mcp-app-${displayMode}`}>
        <div className="mcp-app-error">
          <p>❌ Failed to load MCP App</p>
          <p className="mcp-app-error-detail">{error}</p>
          <button onClick={loadResource}>Retry</button>
        </div>
      </div>
    );
  }

  // Render iframe
  return (
    <div className={`mcp-app-container mcp-app-${displayMode}`}>
      <iframe
        ref={iframeRef}
        className="mcp-app-iframe"
        title="MCP App"
        sandbox="allow-scripts allow-same-origin"
        onLoad={handleIframeLoad}
      />
    </div>
  );
};

export default MCPAppView;
